import { useEffect, useState } from 'react';
import { ChartWheel } from './components/ChartWheel';
import { BirthForm } from './components/BirthForm';
import { DailyCard } from './components/DailyCard';
import { Landing } from './components/Landing';
import { Shop } from './components/Shop';
import { Paywall } from './components/Paywall';
import {
  fetchDailyExtended,
  fetchLoveReading,
  fetchWeekly,
  fetchYearAhead,
  fetchMoonRitual,
  fetchNatalInterpretation,
  type NatalChart,
  type DailyExtended,
  type LoveReading,
  type WeeklyReading,
  type YearAhead,
  type MoonRitualPayload,
  type NatalInterpretation,
} from './api';

type Tab = 'daily' | 'natal' | 'shop';

const BOT_USERNAME = 'yourastrologydailybot';
const LANDING_LANGS = ['pl', 'en', 'ru', 'es', 'pt', 'id', 'fa', 'ar', 'hi', 'uk', 'tr'];

function detectLang(): string {
  const nav = navigator.language?.toLowerCase().split('-')[0] ?? 'en';
  return LANDING_LANGS.includes(nav) ? nav : 'en';
}

function hasTelegramInitData(): boolean {
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp?.initData;
}

export function App() {
  const [tab, setTab] = useState<Tab>('daily');
  const [chart, setChart] = useState<NatalChart | null>(null);
  const [inTelegram, setInTelegram] = useState(false);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    tg?.ready?.();
    tg?.expand?.();
    setInTelegram(hasTelegramInitData());
  }, []);

  if (!inTelegram) {
    return <Landing lang={detectLang()} botUsername={BOT_USERNAME} />;
  }

  return (
    <div className="app">
      <h1>✨ Astrologia</h1>

      <div className="tabs">
        <button className={tab === 'daily' ? 'active' : ''} onClick={() => setTab('daily')}>
          Dziś
        </button>
        <button className={tab === 'natal' ? 'active' : ''} onClick={() => setTab('natal')}>
          Karta
        </button>
        <button className={tab === 'shop' ? 'active' : ''} onClick={() => setTab('shop')}>
          Sklep
        </button>
      </div>

      {tab === 'daily' && <DailyTab />}
      {tab === 'natal' && <NatalTab chart={chart} setChart={setChart} />}
      {tab === 'shop' && <Shop />}
    </div>
  );
}

/** Daily tab = free daily card + 4 paid expansions (love, extended, weekly, moon). */
function DailyTab() {
  return (
    <>
      <DailyCard />
      <PaidBlock
        kind="love"
        title="💞 Horoskop miłosny"
        description="Pogłębione czytanie o uczuciach, relacjach i pragnieniach na dziś."
      />
      <PaidBlock
        kind="extended"
        title="✨ Rozszerzony horoskop"
        description="Trzy obszary życia (miłość, praca, wnętrze) z konkretną radą."
      />
      <PaidBlock
        kind="weekly"
        title="🗓 Mapa tygodnia"
        description="Siedmiodniowy odczyt — tematy, kluczowe dni, ostrożne momenty."
      />
      <PaidBlock
        kind="moon"
        title="🌙 Rytuał Księżyca"
        description="Rytuał dopasowany do aktualnej fazy Księżyca."
      />
    </>
  );
}

function NatalTab({ chart, setChart }: { chart: NatalChart | null; setChart: (c: NatalChart | null) => void }) {
  return (
    <>
      {!chart && <BirthForm onComputed={setChart} />}
      {chart && (
        <>
          <div className="chart-wheel-wrapper">
            <ChartWheel chart={chart} />
          </div>
          <div className="card">
            <div className="accent" style={{ fontWeight: 600, marginBottom: 8 }}>
              Słońce: {chart.sunSign} · Księżyc: {chart.moonSign}
              {chart.ascendantSign && ` · ASC: ${chart.ascendantSign}`}
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              {chart.solarChartFallback
                ? 'Karta solarna (godzina nieznana) — Ascendent i domy nieobliczone.'
                : `${chart.aspects.length} aspektów wykrytych.`}
            </div>
          </div>

          <PaidBlock
            kind="natal"
            title="🪐 Pełna interpretacja karty"
            description="Wszystkie planety w znakach i domach + kluczowe aspekty. Odblokowane na zawsze."
          />

          <PaidBlock
            kind="year"
            title="🗝 Rok do przodu"
            description="12-miesięczny odczyt tranzytów: tematy roku, miesiące mocy, punkty zwrotne."
          />

          <button className="btn secondary" onClick={() => setChart(null)} style={{ marginTop: 12 }}>
            Nowa karta
          </button>
        </>
      )}
    </>
  );
}

type PaidKind = 'love' | 'extended' | 'weekly' | 'moon' | 'year' | 'natal';

const PAID_META: Record<PaidKind, { productId: string; priceStars: number }> = {
  love: { productId: 'love-daily', priceStars: 30 },
  extended: { productId: 'daily-extended', priceStars: 50 },
  weekly: { productId: 'weekly-cosmic', priceStars: 99 },
  moon: { productId: 'moon-ritual', priceStars: 79 },
  year: { productId: 'year-ahead', priceStars: 499 },
  natal: { productId: 'natal-full', priceStars: 299 },
};

function PaidBlock({ kind, title, description }: { kind: PaidKind; title: string; description: string }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<
    | { kind: 'love'; v: LoveReading }
    | { kind: 'extended'; v: DailyExtended }
    | { kind: 'weekly'; v: WeeklyReading }
    | { kind: 'moon'; v: MoonRitualPayload }
    | { kind: 'year'; v: YearAhead }
    | { kind: 'natal'; v: NatalInterpretation }
    | null
  >(null);
  const [locked, setLocked] = useState<boolean | null>(null);

  async function tryOpen() {
    if (data) { setExpanded((e) => !e); return; }
    setLoading(true);
    try {
      switch (kind) {
        case 'love': {
          const r = await fetchLoveReading();
          if (r.ok) { setData({ kind: 'love', v: r.data }); setExpanded(true); setLocked(false); }
          else setLocked(true);
          break;
        }
        case 'extended': {
          const r = await fetchDailyExtended();
          if (r.ok) { setData({ kind: 'extended', v: r.data }); setExpanded(true); setLocked(false); }
          else setLocked(true);
          break;
        }
        case 'weekly': {
          const r = await fetchWeekly();
          if (r.ok) { setData({ kind: 'weekly', v: r.data }); setExpanded(true); setLocked(false); }
          else setLocked(true);
          break;
        }
        case 'moon': {
          const r = await fetchMoonRitual();
          if (r.ok) { setData({ kind: 'moon', v: r.data }); setExpanded(true); setLocked(false); }
          else setLocked(true);
          break;
        }
        case 'year': {
          const r = await fetchYearAhead();
          if (r.ok) { setData({ kind: 'year', v: r.data }); setExpanded(true); setLocked(false); }
          else setLocked(true);
          break;
        }
        case 'natal': {
          const r = await fetchNatalInterpretation();
          if (r.ok) { setData({ kind: 'natal', v: r.data }); setExpanded(true); setLocked(false); }
          else setLocked(true);
          break;
        }
      }
    } finally {
      setLoading(false);
    }
  }

  if (locked) {
    return <Paywall productId={PAID_META[kind].productId} title={title} priceStars={PAID_META[kind].priceStars} description={description} onPaid={() => { setLocked(null); tryOpen(); }} />;
  }

  if (!expanded || !data) {
    return (
      <div className="card paid-teaser" onClick={tryOpen} role="button">
        <div className="paid-teaser-title">{title}</div>
        <div className="paid-teaser-desc">{description}</div>
        <div className="paid-teaser-cta">{loading ? '…' : 'Otwórz →'}</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="accent" style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
      {data.kind === 'love' && <div className="daily-reading">{data.v.reading}</div>}
      {data.kind === 'extended' && (
        <>
          <h3>💞 Miłość</h3><div className="daily-reading">{data.v.sections.love}</div>
          <h3>💼 Praca</h3><div className="daily-reading">{data.v.sections.career}</div>
          <h3>🌿 Wnętrze</h3><div className="daily-reading">{data.v.sections.inner}</div>
        </>
      )}
      {data.kind === 'weekly' && <div className="daily-reading">{data.v.reading}</div>}
      {data.kind === 'moon' && (
        <>
          <div className="muted">{data.v.entry.name} · {data.v.entry.short}</div>
          <div className="daily-reading" style={{ marginTop: 8 }}>{data.v.entry.ritual}</div>
        </>
      )}
      {data.kind === 'year' && <div className="daily-reading">{data.v.reading}</div>}
      {data.kind === 'natal' && (
        <>
          {data.v.sections.map((s, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <h3>{s.title}</h3>
              <div className="daily-reading">{s.body}</div>
            </div>
          ))}
          {data.v.aspects.length > 0 && (
            <>
              <h3>Aspekty</h3>
              <ul className="aspect-list">
                {data.v.aspects.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
